<template>
  <div v-if="showBuyLeads" class="container-fluid page-container">
    <template v-if="mobileBasket">
      <div class="row">
        <div class="col-12 col-md-9 col-lg-8 m-auto">
          <b-card
            class="overflow-hidden bg-white border basket"
          >
            <b-card-header class="d-flex align-items-center">
              <div class="col-12 px-0 d-flex align-items-center">
                <h2 class="text-center m-auto h4">
                  {{ $t('Bulk Buy') }}
                </h2>
              </div>
              <b-button class="close position-absolute m-auto" variant="link" @click="mobileBasket = false">
                <font-awesome-icon icon="times" />
              </b-button>
            </b-card-header>
            <b-card-body class="py-3 px-4">
              <bulk-buy
                @cancel="mobileBasket = false"
                @remove="removeLead"
              />
            </b-card-body>
          </b-card>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col d-flex page-header__container mb-4 pb-3 justify-content-between">
          <page-heading :class="'align-items-center'" :title="pageTitle" />
          <div class="d-flex">
            <result-list-control
              :sortFields="isMobile ? [defaultSortOption, ...tableFields] : null"
              :filterOptions="filterOptions"
              :store-key="filterKey"
              :coverageOptions="coverageOptions"
            >
              <template v-slot:icons>
                <buy-leads-status-icons v-if="!isMobile" />
                <div class="filter-item separator bulk-buy ml-1">
                  <b-button size="sm" variant="outline-secondary" :class="{'alt': bulk}" :disabled="!canPayForLeads" @click="bulk = !bulk">
                    <template v-if="isMobile">
                      <span id="buy-leads-bulk-cta" v-b-tooltip.hover>
                        <font-awesome-icon v-if="bulk" :icon="'pound-sign'" transform="shrink-3" />
                        <font-awesome-layers v-else>
                          <font-awesome-icon :icon="'pound-sign'" transform="left-3 up-3 shrink-1" class="primary" />
                          <font-awesome-icon :icon="'pound-sign'" transform="right-3 down-3 shrink-2" />
                        </font-awesome-layers>
                      </span>
                    </template>
                    <template v-else>
                      <span id="buy-leads-bulk-cta" v-b-tooltip.hover>
                        <font-awesome-icon :icon="'shopping-basket'" aria-hidden="true" />
                        {{ $t('Bulk Buy') }}
                      </span>
                    </template>
                  </b-button>
                  <b-tooltip v-if="!canPayForLeads" target="buy-leads-bulk-cta">
                    {{ $t('Please add a card') }}
                    <router-link :to="{ path: '/help/contact' }">
                      {{ $t('here') }}
                    </router-link>
                  </b-tooltip>
                </div>
              </template>
            </result-list-control>
          </div>
        </div>
      </div>
      <div class="row">
        <list-accordion v-if="isMobile">
          <template v-slot:list>
            <buy-leads-status-icons :list-container-class="'none'" />
          </template>
        </list-accordion>
      </div>
      <mobile-bulk-banner v-if="bulk && isMobile" class="position-sticky bulk-container" @mobile-basket="mobileBasket=true" />
      <div class="row">
        <div class="col marketplace-leads-container">
          <marketplace-leads-list v-if="isMobile" :fields="tableFields" :disable="bulkPurchaseComplete" />
          <marketplace-leads-table v-else ref="leadsTable" :fields="tableFields" />
        </div>
        <lead-basket
          v-if="showBasket"
          @refresh="refresh"
          @purchased="bulkPurchaseComplete=true"
        />
      </div>
    </template>
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import ResultListControl from "@/components/control/ResultListControl";
import MarketplaceLeadsTable from "@/components/table/MarketplaceLeadsTable";
import {UPDATE_FILTER} from "@/store/mutation-types";
import LeadBasket from "@/components/pane/LeadBasket";
import MarketplaceLeadsList from "@/components/list/MarketplaceLeadsList";
import {BULK_BUY, CLEAR_SELECTED_LEADS, PRE_SELECT, SELECTED_LEADS} from "@/store/leads/mutation-types";
import userDevice from "@/imports/mixins/userDevice";
import BuyLeadsStatusIcons from "@/components/list/icons/BuyLeadsStatusIcons";
import ListAccordion from "@/components/accordion/ListAccordion";
import MobileBulkBanner from "@/components/pane/MobileBulkBanner";
import BulkBuy from "@/components/pane/BulkBuy";
import gaEvent from "@/imports/mixins/gaEvent";
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";
import companyAccount from "@/imports/mixins/companyAccount";

const IN_COVERAGE = 1;
const OUT_OF_COVERAGE = 2;
const ALL_COVERAGE = 3;
const POSTCODE = 4;

export default {
  name: "BuyLeads",
  title: 'Buy Leads Marketplace',
  components: {
    BulkBuy,
    MobileBulkBanner,
    MarketplaceLeadsList,
    LeadBasket, MarketplaceLeadsTable, ResultListControl, PageHeading, BuyLeadsStatusIcons, ListAccordion
  },
  mixins: [userDevice, gaEvent, company, workTypeTranslation, companyAccount],
  data: function () {
    return {
      filterKey: 'buyLeads',
      mobileBasket: false,
      bulkPurchaseComplete: false,
    }
  },
  computed: {
    pageTitle: function() {
      return this.$t("Buy Leads");
    },
    filterOptions: function() {
      return [
        {
          key: 'sold', label: this.$t('Leads Sold'), values:
            [
              {text: this.$tc('{n}/{max} Leads Sold', 1, { max: 3}), value: 0},
              {text: this.$tc('1/{max} Leads Sold', 1, { max: 3}), value: 1},
              {text: this.$tc('{n}/{max} Leads Sold', 2, { max: 3}), value: 2},
            ]
        },
        {key: 'workType', label: 'Lead Type', values: []},
      ];
    },
    tableFields: function () {
      return [
        {
          key: 'date',
          label: this.$t('Date & Time'),
          canSort: true,
          class: 'first-col date-column',
          sortable: true,
          customSortLabels: [this.$t('Newest Lead'), this.$t('Oldest Lead')],
          invertOrder: true
        },
        {
          key: 'postcode',
          label: this.$t('Postcode'),
          formatter: 'postcode',
          canSort: true,
          sortable: true,
          customSortLabels: [this.$t('Work Type A-Z'), this.$t('Work Type Z-A')],
          class: 'postcode-column'
        },
        {key: 'workType', label: this.$t('Lead Type'), formatter: 'workType', canSort: false, class: 'worktype-column'},
        {
          key: 'timescale',
          label: this.$t('Timescale'),
          formatter: 'timescale',
          canSort: true,
          sortable: true,
          customSortLabels: [this.$t('Most Urgent'), this.$t('Least Urgent')]
        },
        {key: 'sold', label: this.$t('Leads Sold'), canSort: true, sortable: true, customSortLabels: [this.$t('Lowest Leads Sold'), this.$t('Highest Leads Sold')]},
        {key: 'dashboardPrice', label: this.$t('Price'), canSort: true, sortable: true, customSortLabels: [this.$t('Lowest Price'), this.$t('Highest Price')]},
        {key: 'buy', label: ''},
      ]
    },
    coverageOptions: function() {
      return [
        {value: ALL_COVERAGE, text: this.$t('All Coverage'), input: false},
        {value: IN_COVERAGE, text: this.$t('Within my coverage area'), input: false},
        {value: OUT_OF_COVERAGE, text: this.$t('Outside my coverage area'), input: false},
        {value: POSTCODE, text: this.$t('By Postcode'), input: true},
      ];
    },
    defaultSortOption: function() {
      return {key: null, label: this.$t('Default (Relevance)'), canSort: true};
    },
    bulk: {
      get: function () {
        return this.$store.state.leads.BULK_BUY;
      },
      set: function (value) {
        this.$store.commit(BULK_BUY, value);
      }
    },
    showBasket: function () {
      return (this.$store.state.leads.SELECTED_LEADS.length > 0 && !this.isMobile) || (this.bulk && !this.isMobile);
    },
    selectedLeads: {
      get: function () {
        return this.$store.state.leads.SELECTED_LEADS;
      },
      set: function (value) {
        this.$store.commit(SELECTED_LEADS, value);
      }
    },
  },
  methods: {
    loadAvailableWorkTypes: async function () {
      let workTypeRequest = await this.$api.leads.marketplaceWorkTypes();
      let options = [
        {
          label: this.$t("My Work Types"),
          value: null,
          children: [],
        },
        {
          label: this.$t("Similar Work Types"),
          value: 0,
          children: [],
        }
      ];

      workTypeRequest.data.data.forEach((workType) => {
        let category = options.find((optionCategory) => optionCategory.label === workType.category.name);
        if (category === undefined) {
          category = {
            label: workType.category.name,
            children: [],
          }
          options.push(category);
        }
        let subCategory = category.children.find((sub) => sub.real === workType.work_type_category);
        if (subCategory === undefined) {
          subCategory = {
            real: workType.work_type_category,
            label: this.getCategoryTranslation(workType),
            children: [],
          }
          category.children.push(subCategory);
        }
        subCategory.children.push({
          text: this.getDescriptionTranslation(workType, this.company?.communications_language?.iso_code ?? 'en'),
          value: workType.id
        });
      });
      this.filterOptions.find((object) => object.key === 'workType').values = options;
    },
    refresh: function () {
      this.$refs.leadsTable.refresh();
    },
    checkPreSelect: function () {
      let filters = this.$store.getters.getFilter(this.filterKey);
      if (this.$route.query.leadcode || this.$route.query.enquirycode) {
        if (this.$route.query.leadcode && this.$route.query.leadcode.length > 0) {
          filters = {...filters, 'lead': this.$route.query.leadcode};
        } else if (this.$route.query.enquirycode && this.$route.query.enquirycode.length > 0) {
          filters = {...filters, 'enquiry': this.$route.query.enquirycode};
        } else if (this.$route.query.notifiercode && this.$route.query.notifiercode.length > 0) {
          filters = {...filters, 'notifierCode': this.$route.query.notifiercode};
        }
        this.$store.commit(PRE_SELECT, true);
        this.$store.commit(
          UPDATE_FILTER,
          {
            group: this.filterKey,
            filters: filters
          }
        );
        this.checkOrigin();
      } else {
        this.removeOrigin();
        if (filters.lead) {
          delete (filters.lead);
        }
        if (filters.enquiry) {
          delete (filters.enquiry);
        }
        if (filters.notifierCode) {
          delete (filters.notifierCode);
        }
        this.$store.commit(PRE_SELECT, false);
        this.$store.commit(
          UPDATE_FILTER,
          {
            group: this.filterKey,
            filters: {...filters}
          }
        );
      }
    },
    checkOrigin: function() {
      this.origin = this.$route.query.sms ? 'sms' : (this.$route.query.r ? 'email' : null);
    },
    removeLead: function(lead) {
      this.selectedLeads = [... this.selectedLeads.filter((selectedLead) => selectedLead[0].id !== lead[0].id)];
    }
  },
  watch: {
    isMobile: function(value) {
      if (value === true) {
        this.bulk = false;
      }
    },
    bulk: function(value) {
      this.bulkPurchaseComplete = false;
      this.origin = null;
      if (!value) {
        this.$store.commit(CLEAR_SELECTED_LEADS);
      }
    },
    mobileBasket: function(value) {
      if (!value) {
        this.bulk = false;
      }
    },
    selectedLeads: function(value) {
      if (value.length === 0) {
        this.mobileBasket = false;
      }
    },
    company: function(value) {
      if (value !== null) {
        if (this.$store.state.company.COMPANY?.account?.show_buy_leads === false) {
          this.$router.push({name:'dashboard'});
        }
      }
    }
  },
  created: function () {
    this.bulk = false;
    this.loadAvailableWorkTypes();
    this.$store.commit(CLEAR_SELECTED_LEADS);
    this.checkPreSelect();
    this.$eb.$on('leadNotification-loadNewEnquiry', () => {
      this.checkPreSelect();
    });
  },
}
</script>

<style scoped lang="scss">
.primary {
  color: $theme-primary-blue-600;
}

@media only screen and (max-width: 1029px) {
  .bulk-buy {
    .btn {
      width: 31px;
    }
  }
}

.bulk-container {
  top: 80px;
  z-index: 3;
}

::v-deep {
  .buy-lead {
    background: $theme-primary-accents-900;
    font: $font-size-14;

    &__btn {
      min-width: 90px;
    }

    &__bought {
      h2,
      p,
      .text-link {
        font-size: $font-size-14;
      }
    }
  }

  .lead-actions {
    .btn {
      min-width: 90px;
    }
  }

  @media only screen and (min-width: 1061px) and (max-width: 1800px) {
    .buy-lead__bought,
    .lead-actions-bought {
      padding-left: 0;
    }
  }

  .list-control {

    @media only screen and (max-width: 1479px) {
      &__desktop {
        flex-direction: column;

        .bulk-buy {
          &:before {
            content: none;
          }
        }

        .list-icons {
          display: flex;
          justify-content: flex-end;
          margin-bottom: .5rem;
        }
      }
    }

    &__mobile {
      .filter-icon {
        margin-right: 15px;
        margin-left: 10px;
        width: 25px;
        padding: 0 10px;
      }
    }
  }

  .table thead,
  .table tbody tr {

    .worktype-column {
      @media only screen and (max-width: 1199px) {
        max-width: 120px;
      }

      @media only screen and (min-width: 1199px) and (max-width: 1429px) {
        max-width: 130px;
      }

      @media only screen and (min-width: 1430px) {
        max-width: 150px;
      }
    }

    .postcode-column {
      @media only screen and (max-width: 1199px) {
        max-width: 115px;
      }
      .postcode {
        word-break: break-all;
      }
    }

    .date-column {
      @media only screen and (max-width: 1199px) {
        max-width: 90px;
      }

      @media only screen and (min-width: 1200px) {
        max-width: 110px;
      }
    }

    .qualified-method {
      max-width: 110px;
      @media only screen and (min-width: 1060px) and (max-width: 1480px) {
        div {
          display: none;
        }
      }
    }
  }
}

@media only screen and (max-width: 1060px) {
  .separator {
    &:before {
      width: 0;
    }
  }
}

@media only screen and (max-width: 414px) {
  ::v-deep .list-icons-container {
    .icon-container {
      margin-top: 10px;
    }
  }
}

@media only screen and (max-device-width : 374px) {
  ::v-deep .list-icons-container {
    flex-direction: column;

    .last-column {
      margin-left: 0 !important;
    }
  }
}
</style>
